import React, { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { IApiResponse } from 'utils/models';
import Form from 'ui/Form/Form';
import { requiredOnly, validateAccount, validationCallback } from 'utils/validation';
import { AccountInput } from 'ui/Input';
import Input from 'ui/Input/Input';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Button, { Sizes as ButtonSizes, Themes as ButtonThemes } from 'ui/Button/Button';
import { StatusModal, WarningModal } from 'components/modals';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import api from 'api/api';
import InputHint from 'components/modules/Beneficiaries/InputHint/InputHint';
import debounce from 'utils/debounce';
import { IBeneficiaryLocal } from './BenFab.model';

import deleteIcon from 'assets/icons/delete.png';

const schema = yup.object().shape({
  fullName: requiredOnly('Full name'),
  accountNumber: validateAccount(),
});

interface IFormValues {
  fullName: string;
  accountNumber: string;
}

interface IEditBenFab {
  data: IBeneficiaryLocal;
  onClose: () => void;
}

const EditBenFab = ({ data, onClose }: IEditBenFab) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const [actionType, setActionType] = useState<string>('');
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [nameHint, setNameHint] = useState('');
  const [isLookingUp, setLookingUp] = useState(false);
  const clearNameHint = () => setNameHint('');
  const [validatedAccountNo, setValidatedAccountNo] = useState(true);


  const lookupAccount = (accountNumber: string) => {
    api
      .get(`transfer/lookupaccount?accountNumber=${accountNumber}`, {}, true, true)
      .then((response) => {
        setNameHint(response.accountName);
        setValidatedAccountNo(true);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setNameHint(error.description);
        setValidatedAccountNo(false);
      })
      .finally(() => setLookingUp(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeAccountNumber = ({ target }) => {
    setLookingUp(true);
    debounced(target.value as string);
  };

  const openWarning = (type: string) => () => {
    setActionType(type);
    setShowWarning(true);
  };
  const closeWarning = () => {
    setShowWarning(false);
  };

  const [showStatus, setShowStatus] = useState(false);
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const validate = useCallback((values: IFormValues) => validationCallback<IFormValues>(schema)(values), []);

  const intialValues: IFormValues = useMemo(() => {
    const iniitalData = {
      fullName: data.name,
      accountNumber: data.accountNumber,
    };

    return iniitalData;
  }, []);

  const onDeleteBeneficiary = (id: number) => {
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/transfer/delete', {
        beneficiaryId: id,
      })
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const onSaveBeneficiary = (id: number, values: IFormValues) => {
    const body = {
      ...values,
      beneficiaryId: id,
    };

    api
      .put('/beneficiary/transfer/update', body)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const openConfirm = (values: IFormValues) => () => {
    setSubmitStatus(AxiosStatus.PENDING);

    return actionType === 'delete' ? onDeleteBeneficiary(data.id) : onSaveBeneficiary(data.id, values);
  };

  return (
    <>
      <Form
        onSubmit={openWarning('save')}
        validate={validate}
        initialValues={intialValues}
        render={({ handleSubmit, values, form, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            {!downMdScreen && <NavigateBackStep className="mt30" onClick={onClose}>Back</NavigateBackStep>}
            <FieldsLeveler
              label={(
                <>
                  <div>Beneficiary details</div>
                  {!downMdScreen && (
                    <Button size={ButtonSizes.small} onClick={openWarning('delete')}>
                      <img src={deleteIcon} alt="trash-icon" />
                      &nbsp;Delete
                    </Button>
                  )}
                </>
        )}
            >
              <Input name="fullName" type="text" label="Full Name" disabled={submitting} />
              <AccountInput
                name="accountNumber"
                label="Account Number"
                disabled={submitting}
                onInput={handleChangeAccountNumber}
              />
              {(nameHint || isLookingUp) && <InputHint hint={nameHint} loading={isLookingUp} onClose={clearNameHint} />}
            </FieldsLeveler>
            <SubmitLeveler>
              {downMdScreen && (
                <Button
                  type="button"
                  theme={ButtonThemes.secondary}
                  fullWidth
                  className="mb20"
                  onClick={openWarning('delete')}
                >
                  Delete Beneficiary
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                disabled={
                  hasValidationErrors || submitting || !form.getState().dirty || isLookingUp || !validatedAccountNo
                }
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </SubmitLeveler>
            <WarningModal
              open={showWarning}
              submitting={submitting}
              submitName={actionType === 'delete' ? 'Delete' : 'Save'}
              onSubmit={openConfirm(values)}
              onClose={closeWarning}
            >
              {actionType === 'delete'
                ? 'Are you sure you want to delete the Beneficiary?'
                : 'Save changes to Beneficiary?'}
            </WarningModal>
            <StatusModal
              open={showStatus}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onRetry={onCloseStatusModal}
              onClose={onCloseStatusModal}
              onRetryText="Continue"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMessage : errorMessage}
            </StatusModal>
          </form>
        )}
      />
    </>
  );
};

export default EditBenFab;
